import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { LoggerService } from "./logger.service";
import { EventService } from "./event.service";
import { selectedBuilding } from "src/components/shared-components/state/selectors/building.selector";
import { map, take, tap } from "rxjs/operators";
import { Building } from "src/components/shared-components/class/general-classes/building";
import { currentBuildingUnsetAction } from "src/components/shared-components/state/actions/building.action";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Bills } from "src/model/class/general classes/bill";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";

@Injectable({
  providedIn: "root",
})
export class DpBillsService {
  private _idArea: number = null;
  public billsSubject = new BehaviorSubject<Array<any>>([]);

  _eventSubscriptions: Array<Subscription> = [];

  constructor(
    private _http: HttpClient,
    private _store: Store,
    private _eventService: EventService
  ) {
    this.subscribeEvents();
  }

  ngOnInit() {
    this.subscribeEvents();
  }
  ngOnChanges() {
    this.subscribeEvents();
  }

  ngOnDestroy(): void {
    this.unsubscribeEvents();
  }

  /**
   *  Create a new bill
   * @param newBill  New bill to create
   * @returns
   */
  public createBill(newBill: Bills): Promise<Bills> {
    return this._http
      .post<Bills>(
        `${environment.httpServerRoot}${AppSettings.API_URL_BILL}`,
        newBill
      )
      .toPromise();
  }


  /**
   *  Get all Bills from  the server
   * @returns Promise
   */
  public getAllBills(
    excludeExpenseNote?: boolean
  ): Promise<Array<Bills> | number> {
    if (!this._idArea) return Promise.resolve(ApiErrorType.NOT_FOUND);

    let queryString = `?idArea=${this._idArea}`;
    if (excludeExpenseNote) {
      queryString += `&excludeExpensesNote=${excludeExpenseNote}`;
    }
    return this._http
      .get<Array<Bills>>(
        `${environment.httpServerRoot}${AppSettings.API_URL_BILL}/${queryString}`,
        {
          observe: "response",
        }
      )
      .pipe(
        map((responseData: HttpResponse<Object>) => {
          if (responseData.status !== ApiErrorType.OK)
            return responseData.status;
          this.billsSubject.next(responseData.body as Array<Bills>);
          return responseData.body as Array<Bills>;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   * Step 1 of the direct-to-S3 upload: ask the backend for a short-lived
   * presigned PUT URL. No file bytes travel through our API server and no AWS
   * credential ever reaches the client.
   */
  public getUploadUrl(idBill: number, namefile: string): Promise<string> {
    return this._http
      .get<{ url: string }>(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_AWS_BILL}/uploadfile/${idBill}/url?namefile=${encodeURIComponent(
          namefile
        )}`
      )
      .pipe(
        map((response) => response?.url),
        take(1)
      )
      .toPromise();
  }

  /**
   * Step 2 of the direct-to-S3 upload: PUT the raw file straight to S3 over
   * HTTPS using the presigned URL.
   *
   * This intentionally uses the native fetch() API instead of Angular's
   * HttpClient: the AuthenticationInterceptor attaches an `Authorization:
   * Bearer ...` header (and `Content-Type: application/json`) to every
   * HttpClient request, which would break the SigV4-signed S3 request and leak
   * the session token to S3. fetch() bypasses the interceptor entirely.
   */
  public async uploadFileToPresignedUrl(
    url: string,
    file: File
  ): Promise<void> {
    const response = await fetch(url, {
      method: "PUT",
      body: file,
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed with status ${response.status}`);
    }
  }

  /**
   * Step 3 of the direct-to-S3 upload: tell the backend the bytes are on S3 so
   * it binds the file to the bill (same deterministic key the presigned URL
   * was generated for).
   */
  public confirmUpload(idBill: number, namefile: string): Promise<boolean> {
    return this._http
      .put(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_AWS_BILL}/uploadfile/${idBill}?namefile=${encodeURIComponent(
          namefile
        )}`,
        {},
        {
          observe: "response",
        }
      )
      .pipe(
        map((responseData: HttpResponse<Object>) => responseData.ok),
        take(1)
      )
      .toPromise();
  }

  /**
   *  Get an uploaded file for a selected bill
   * @param billId  selected bill id
   * @returns
   */
  public getUploadedFile(billId: number) {
    return this._http
      .get(
        `${environment.httpDPAPIRoot}${environment.anaciPlatformQuote}${AppSettings.API_URL_AWS_BILL}/file/${billId}`
      )
      .pipe(
        map((responseData: any) => {
          return responseData;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   *  Get a single bill from the server
   * @returns Promise
   */
  public getSingleBill(billId: number): Observable<HttpResponse<Object>> {
    const queryString = `?id=${billId}`;
    return this._http
      .get<HttpResponse<Object>>(
        `${environment.httpServerRoot}${AppSettings.API_URL_BILL}/${queryString}`,
        { observe: "response" }
      )
      .pipe(take(1));
  }

  /**
   *  Handle deleting a bill
   * @param idBill  The id of the bill to delete
   * @returns
   */
  public async deleteBill(idBill: number): Promise<HttpResponse<Object>> {
    return this._http
      .delete<number>(
        `${environment.httpServerRoot}${AppSettings.API_URL_BILL}/${idBill}`,
        {
          observe: "response",
        }
      )
      .pipe(
        map((responseData: HttpResponse<Object>) => {
          return responseData;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   *  Update a selected bill
   * @param idSelectedBill  id of the selected bill
   * @param updatedBill  updated bill
   * @returns   Promise
   */
  public updateSelectedBill(
    idSelectedBill: number,
    updatedBill: Bills
  ): Promise<Bills> {
    return this._http
      .put<Bills>(
        `${environment.httpServerRoot}${AppSettings.API_URL_BILL}/${idSelectedBill}`,
        updatedBill
      )
      .toPromise();
  }

  /**
   *  Handle the deleting of file request
   * @param idBill
   * @returns
   */
  public deleteBillFile(idBill: number): Promise<boolean> {
    return this._http
      .delete<number>(
        `${environment.httpServerRoot}${AppSettings.API_URL_BILL}/file/${idBill}`,
        {
          observe: "response",
        }
      )
      .pipe(
        map((responseData) => {
          return responseData.ok;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   * Get the current idArea from the store
   */
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();
    this._eventSubscriptions.push(
      this._store
        .select(selectedBuilding)
        .pipe(
          map((building: Building) => {
            return building?.idAreaLocal;
          }),
          tap((idArea) => {
            this._idArea = idArea;
          })
        )
        .subscribe()
    );

    this._eventSubscriptions.push(
      this._eventService?.OnMakeLogout.subscribe(() => {
        this.deselectCurrentBuilding();
      })
    );
  }

  /**
   * Unsubscribe to event-service observable
   */
  public unsubscribeEvents(): void {
    this._eventSubscriptions.map((subscription) => subscription?.unsubscribe());
  }

  /**
   * Unset the current building
   */
  private deselectCurrentBuilding(): void {
    this._store.dispatch(currentBuildingUnsetAction());
  }
}
